import React, { useRef, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import TypingIndicator from '../TypingIndicator';
import MessageItem from './MessageItem';
import AuthContext from '../../../context/AuthContext';
import { useChatContext } from '../../../context/ChatContext';
import './ChatMessages.css';

/**
 * Normalizes message data to handle inconsistencies.
 */
const normalizeMessage = (raw, currentUserId) => {
    if (!raw) return null;

    const msgId = raw._id || raw.id;
    if (!msgId) return null;

    const createdAt = raw.createdAt || raw.created_at || new Date().toISOString();

    // Normalize Sender
    let sender = raw.sender;
    if (typeof raw.sender === 'string') {
        sender = { _id: raw.sender, username: 'User' };
    } else if (!raw.sender) {
        sender = { _id: 'unknown', username: 'Deleted User' };
    }

    const fromSelf = raw.fromSelf ?? (String(sender._id) === String(currentUserId));
    const readBy = Array.isArray(raw.readBy) ? raw.readBy : [];

    const reactions = Array.isArray(raw.reactions)
        ? raw.reactions.map(r => {
            const emoji = r?.emoji || r?.type || r?.key;
            const users = Array.isArray(r?.users) ? r.users : (r?.user ? [r.user] : []);
            return emoji ? { emoji, users } : null;
        }).filter(Boolean)
        : [];

    return {
        ...raw,
        _id: msgId,
        sender,
        fromSelf,
        reactions,
        readBy,
        createdAt
    };
};

const ChatMessages = ({
    messages,
    typingUsers = [],
    isLoading,
    unreadRef,
    chatEndRef,
    onReply,
    onReact,
    highlightedMessageId = null,
    currentUserId
}) => {
    const containerRef = useRef(null);
    const { user } = useContext(AuthContext);
    const { activeChat } = useChatContext();

    const resolvedUserId = String(currentUserId || user?._id || user?.userId || '');

    const renderDateLabel = useCallback((dateStr) => {
        try {
            const date = parseISO(dateStr);
            if (isNaN(date.getTime())) return null;
            if (isToday(date)) return 'Today';
            if (isYesterday(date)) return 'Yesterday';
            return format(date, 'MMMM d, yyyy');
        } catch (e) {
            return null;
        }
    }, []);

    const enhancedMessages = useMemo(() => {
        if (!Array.isArray(messages)) return [];

        // ✅ Normalize without re-sorting. 
        // We trust the backend's chronological order to maintain scroll anchoring.
        const list = messages.map(m => normalizeMessage(m, resolvedUserId)).filter(Boolean);

        let lastDate = '';
        let foundFirstUnread = false;

        return list.map((msg) => {
            const dateLabel = renderDateLabel(msg.createdAt);
            const showDateSeparator = dateLabel && dateLabel !== lastDate;
            if (showDateSeparator) lastDate = dateLabel;

            const isReadByMe = msg.readBy.some(r => {
                const readerId = r.user?._id || r.user || r;
                return String(readerId) === resolvedUserId;
            });

            const isFirstUnread = !isReadByMe && !msg.fromSelf && !foundFirstUnread;
            if (isFirstUnread) foundFirstUnread = true;

            return {
                ...msg,
                dateLabel,
                showDateSeparator,
                isFirstUnread,
            };
        });
    }, [messages, renderDateLabel, resolvedUserId]);

    return (
        <div ref={containerRef} className="chat-messages-container" role="log">
            <ul className="message-list">
                {/* Loader is at the top of the list for pagination */}
                {isLoading && (
                    <li className="loading-messages-top">
                        <div className="loader-dots">Loading history...</div>
                    </li>
                )}

                {enhancedMessages.map((msg, i) => (
                    <React.Fragment key={`msg-${msg._id}`}>
                        {msg.showDateSeparator && (
                            <li className="date-separator-container">
                                <div className="date-separator">{msg.dateLabel}</div>
                            </li>
                        )}

                        {msg.isFirstUnread && (
                            <li className="unread-separator-container" ref={unreadRef}>
                                <div className="unread-separator">NEW MESSAGES</div>
                            </li>
                        )}

                        <MessageItem
                            msg={msg}
                            currentUserId={resolvedUserId}
                            onReply={onReply}
                            onReact={onReact}
                            activeChatId={activeChat?._id}
                            isHighlighted={highlightedMessageId === msg._id}
                        />
                    </React.Fragment>
                ))}

                <TypingIndicator isVisible={typingUsers.length > 0} typingUsers={typingUsers} />
                <li ref={chatEndRef} style={{ listStyle: 'none', height: '1px' }} />
            </ul>
        </div>
    );
};

ChatMessages.propTypes = {
    messages: PropTypes.array.isRequired,
    typingUsers: PropTypes.array,
    isLoading: PropTypes.bool,
    unreadRef: PropTypes.object,
    chatEndRef: PropTypes.object,
    onReply: PropTypes.func,
    onReact: PropTypes.func.isRequired,
    highlightedMessageId: PropTypes.string,
    currentUserId: PropTypes.string
};

export default ChatMessages;